/**
 * Language core (1.18). German is the master; every other language is only a translation with
 * the same keys (Mike, 09.10.: "Sprache nur als Übersetzung, keine eigenständige Lösung mit
 * Tests"). Own small helpers instead of a library (Mike, 12.10.).
 */
export const LOCALES = ['de', 'en'] as const;
export type Locale = (typeof LOCALES)[number];

/** "de-AT", "en-GB", "EN_us" … → a language we have; anything else → German. */
export function pickLocale(raw: unknown): Locale {
  const lang = typeof raw === 'string' ? raw.toLowerCase().split(/[-_]/)[0] : '';
  return (LOCALES as readonly string[]).includes(lang ?? '') ? (lang as Locale) : 'de';
}

/** BCP 47 tags for dates and numbers – British English (Mike, 12.10.). */
export const LOCALE_TAG: Record<Locale, string> = { de: 'de-DE', en: 'en-GB' };

/** Language names always in their own language, so everyone finds theirs. */
export const LANGUAGE_NAME: Record<Locale, string> = { de: 'Deutsch', en: 'English' };

/** "1 Stern" / "3 Sterne": the form the language's plural rules pick for n. */
export function plural(locale: Locale, n: number, forms: { one: string; other: string }): string {
  return new Intl.PluralRules(LOCALE_TAG[locale]).select(n) === 'one' ? forms.one : forms.other;
}

/** "Milch, Brot und Eier" / "milk, bread and eggs" – the language's own list words. */
export function joinList(items: string[], locale: Locale): string {
  return new Intl.ListFormat(LOCALE_TAG[locale], { type: 'conjunction' }).format(items);
}

/** Weekday names, Monday first (1 = Monday … 7 = Sunday → index n − 1). */
export const WEEKDAY_SHORT: Record<Locale, readonly string[]> = {
  de: ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'],
  en: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
};
export const WEEKDAY_LONG: Record<Locale, readonly string[]> = {
  de: ['Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag', 'Sonntag'],
  en: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
};

/** "3.10." / "3 Oct" for a YYYY-MM-DD day – short, without the year. */
export function dayMonth(day: string, locale: Locale): string {
  const d = Number(day.slice(8, 10));
  const m = Number(day.slice(5, 7));
  if (locale === 'de') return `${d}.${m}.`;
  const month = new Intl.DateTimeFormat('en-GB', { month: 'short', timeZone: 'UTC' }).format(
    new Date(Date.UTC(2000, m - 1, 1)),
  );
  return `${d} ${month}`;
}

/** "18 Uhr" / "6 pm" for HH:MM – a whole hour as people say it. */
export function hourLabel(hhmm: string, locale: Locale): string {
  const h = Number(hhmm.slice(0, 2));
  if (locale === 'de') return `${h} Uhr`;
  return h === 0 ? 'midnight' : h === 12 ? 'noon' : `${h % 12} ${h < 12 ? 'am' : 'pm'}`;
}

/**
 * Every text of a dictionary with its path: { 'kinds.bug.label': '…' }. Texts with blanks
 * (`(name) => …`) are filled with {0}, {1} … so tests and the review page can read them.
 */
export function flattenTexts(tree: unknown, path = ''): Record<string, string> {
  if (typeof tree === 'string') return { [path]: tree };
  if (typeof tree === 'function') {
    const blanks = Array.from({ length: tree.length }, (_, i) => `{${i}}`);
    return { [path]: String((tree as (...a: string[]) => unknown)(...blanks)) };
  }
  if (Array.isArray(tree))
    return Object.assign({}, ...tree.map((v, i) => flattenTexts(v, `${path}[${i}]`)));
  if (tree && typeof tree === 'object')
    return Object.assign(
      {},
      ...Object.entries(tree).map(([k, v]) => flattenTexts(v, path ? `${path}.${k}` : k)),
    );
  return {};
}

/** Short, stable fingerprint of a German text (FNV-1a) – what an English text was made from. */
export function fingerprint(text: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < text.length; i++) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return (h >>> 0).toString(36);
}

/** Fingerprints of all German texts of all dictionaries: { 'feedback.title': 'abc123' }. */
export function fingerprints(dicts: Record<string, { de: unknown }>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [name, d] of Object.entries(dicts))
    for (const [k, v] of Object.entries(flattenTexts(d.de))) out[`${name}.${k}`] = fingerprint(v);
  return out;
}

/** Texts whose German changed (or is new) since the English was last brought up to date. */
export function staleTexts(
  current: Record<string, string>,
  lock: Record<string, string>,
): string[] {
  return Object.keys(current).filter((k) => lock[k] !== current[k]);
}

/**
 * Where a translation differs in shape from the master: missing or extra keys, lists of another
 * length, a text where the master has a group. Empty = same keys. For one-line key tests.
 */
export function shapeDiff(master: unknown, other: unknown, path = ''): string[] {
  const kind = (v: unknown) =>
    Array.isArray(v) ? 'list' : v && typeof v === 'object' ? 'group' : 'text';
  if (kind(master) !== kind(other))
    return [`${path || '(root)'}: ${kind(master)} ≠ ${kind(other)}`];
  if (Array.isArray(master) && Array.isArray(other)) {
    if (master.length !== other.length)
      return [`${path}: ${master.length} entries ≠ ${other.length}`];
    return master.flatMap((m, i) => shapeDiff(m, other[i], `${path}[${i}]`));
  }
  if (kind(master) === 'group') {
    const m = master as Record<string, unknown>;
    const o = other as Record<string, unknown>;
    const at = (k: string) => (path ? `${path}.${k}` : k);
    return [
      ...Object.keys(m).flatMap((k) =>
        k in o ? shapeDiff(m[k], o[k], at(k)) : [`${at(k)}: missing`],
      ),
      ...Object.keys(o)
        .filter((k) => !(k in m))
        .map((k) => `${at(k)}: extra`),
    ];
  }
  return [];
}

/**
 * Website pages the server links to, per language – the same paths as apps/site/src/routes.ts
 * (a test compares both). German without prefix, English under /en (Mike, 12.10.).
 */
export const SITE_PATHS: Record<
  Locale,
  { home: string; confirmed: string; unsubscribed: string; imprint: string; privacy: string }
> = {
  de: {
    home: '/',
    confirmed: '/warteliste/bestaetigt',
    unsubscribed: '/warteliste/abgemeldet',
    imprint: '/impressum',
    privacy: '/datenschutz',
  },
  en: {
    home: '/en',
    confirmed: '/en/waitlist/confirmed',
    unsubscribed: '/en/waitlist/unsubscribed',
    imprint: '/en/imprint',
    privacy: '/en/privacy',
  },
};
